import { useDispatch, useSelector } from 'react-redux'
import { AllState } from '../../store'
import {
  GroupHeaderLayout,
  WbsItemAdditionalPropertyLayoutEntity,
} from '../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { useCallback, useMemo, useRef } from 'react'
import { fetchWbsItemAdditionalProperties } from '../../store/wbsItemAdditionalProperty'
import { EntitySearchReferenceEntity } from '../../domain/entity/WbsItemAdditionalPropertyEntity'
import { ReferencedEntityWithIcon } from '../../domain/value-object/ReferencedEntity'
import { useWbsItemRepository } from '../../services/adaptors/wbsItemRepositoryAdaptor'
import { useProjectMemberRepository } from '../../services/adaptors/projectMemberRepositoryAdaptor'
import { useTeamRepository } from '../../services/adaptors/teamRepositoryAdaptor'
import { WbsItemType } from '../../domain/entity/WbsItemEntity'
import { WbsItemTypeVO } from '../../domain/value-object/WbsItemTypeVO'
import { useTicketRepository } from '../../services/adaptors/ticketRepositoryAdaptor'
import { BaseWbsItemType } from '../../store/project'
import { useProjectPrivateContext } from '../context/projectContext'

// Filtered by wbs item types.
export const useWbsItemAdditionalPropertiesOfBaseWbsItemTypes = () => {
  const { projectUuid } = useProjectPrivateContext()
  const baseWbsItemTypes = useSelector<AllState, BaseWbsItemType>(
    state => state.project.wbsItemTypes
  )
  const baseWbsItemTypeUuids = useMemo(
    () => baseWbsItemTypes.getAll().map(v => v.uuid),
    [baseWbsItemTypes]
  )
  const state = useWbsItemAdditionalProperties(
    projectUuid,
    baseWbsItemTypeUuids
  )

  return state
}
export const useWbsItemAdditionalProperties = (
  projectUuid: string,
  wbsItemTypeUuids?: string[]
): {
  fetched: boolean
  wbsItemAdditionalProperties: WbsItemAdditionalPropertyLayoutEntity | undefined
} => {
  const stateOfProject = useSelector<
    AllState,
    | {
        fetchedAt: number
        wbsItemAdditionalProperties:
          | WbsItemAdditionalPropertyLayoutEntity
          | undefined
      }
    | undefined
  >(state => state.wbsItemAdditionalProperty[projectUuid])
  const fetching = useRef(false)
  const dispatch = useDispatch()

  const filteredByWbsItemTypes = useMemo<
    WbsItemAdditionalPropertyLayoutEntity | undefined
  >(() => {
    if (!stateOfProject || !stateOfProject.wbsItemAdditionalProperties) {
      return
    }
    fetching.current = false
    if (!wbsItemTypeUuids || wbsItemTypeUuids.length === 0) {
      return stateOfProject.wbsItemAdditionalProperties
    }
    const original = stateOfProject.wbsItemAdditionalProperties
    const groupHeaderLayouts: GroupHeaderLayout[] = []
    // Filter properties by wbs item types.
    original.groupHeaderLayouts.forEach(groupHeaderLayout => {
      if (!groupHeaderLayout.propertyLayouts) return
      const propertyLayouts = groupHeaderLayout.propertyLayouts.filter(v => {
        const associatedWbsItemTypes = v.wbsItemAdditionalProperty.wbsItemTypes
        return (
          !associatedWbsItemTypes ||
          associatedWbsItemTypes.length === 0 ||
          associatedWbsItemTypes.some(associatedWbsItemType =>
            wbsItemTypeUuids.includes(associatedWbsItemType.uuid)
          )
        )
      })
      // Ignore group headers does not have child properties.
      if (propertyLayouts.length > 0) {
        groupHeaderLayouts.push({
          groupHeader: groupHeaderLayout.groupHeader,
          displayOrder: groupHeaderLayout.displayOrder,
          propertyLayouts,
        })
      }
    })
    return {
      projectUuid: original.projectUuid,
      groupHeaderLayouts,
    }
  }, [stateOfProject, wbsItemTypeUuids])

  // Handle fetching logic (side effects outside useMemo)
  if (!stateOfProject?.fetchedAt && !fetching.current) {
    fetching.current = true
    dispatch(fetchWbsItemAdditionalProperties(projectUuid))
  }

  // Determine if fetched
  const fetched = !!(stateOfProject?.fetchedAt && !fetching.current)

  // Memoize the result to prevent unnecessary re-renders
  const result = useMemo(
    () => ({
      fetched,
      wbsItemAdditionalProperties: fetched ? filteredByWbsItemTypes : undefined,
    }),
    [fetched, filteredByWbsItemTypes]
  )

  return result
}

export type EntitySearchReferenceEntityRepository = {
  search: (
    projectUuid: string,
    text?: string
  ) => Promise<ReferencedEntityWithIcon[]>
}
export type GetEntitySearchReferenceEntityRepository = (
  entitySearchReferenceEntity: EntitySearchReferenceEntity,
  entitySearchReferenceEntityTicketType?: string
) => EntitySearchReferenceEntityRepository
export const useEntitySearchReferenceEntityRepositories = (
  projectUuid: string
) => {
  const { searchAsReferencedEntity: searchWbsItem } = useWbsItemRepository()
  const { searchAsReferencedEntity: searchTicket } = useTicketRepository()
  const { searchAsReferencedEntity: searchProjectMember } =
    useProjectMemberRepository()
  const { fetchListAsReferencedEntity: fetchTeams } = useTeamRepository()
  const getEntitySearchReferenceEntityRepository: GetEntitySearchReferenceEntityRepository =
    useCallback(
      (
        entitySearchReferenceEntity: EntitySearchReferenceEntity,
        entitySearchReferenceEntityTicketType?: string
      ) => {
        switch (entitySearchReferenceEntity) {
          case EntitySearchReferenceEntity.PROCESS:
            return {
              search: (text?: string) =>
                searchWbsItem(projectUuid, [WbsItemType.PROCESS], text || ''),
            }
          case EntitySearchReferenceEntity.DELIVERABLE_LIST:
            return {
              search: (text?: string) =>
                searchWbsItem(
                  projectUuid,
                  [WbsItemType.DELIVERABLE_LIST],
                  text || ''
                ),
            }
          case EntitySearchReferenceEntity.DELIVERABLE:
            return {
              search: (text?: string) =>
                searchWbsItem(
                  projectUuid,
                  [WbsItemType.DELIVERABLE],
                  text || ''
                ),
            }
          case EntitySearchReferenceEntity.TICKET:
            return {
              search: (text?: string) =>
                entitySearchReferenceEntityTicketType
                  ? searchTicket(
                      projectUuid,
                      [entitySearchReferenceEntityTicketType],
                      text || ''
                    )
                  : Promise.resolve([]),
            }
          case EntitySearchReferenceEntity.TEAM:
            return {
              search: (text?: string) => fetchTeams(projectUuid),
            }
          case EntitySearchReferenceEntity.PROJECT_MEMBER:
            return {
              search: (text?: string) =>
                searchProjectMember(projectUuid, text || ''),
            }
          default:
            reportError(
              `This entity search reference entity(=${entitySearchReferenceEntity}) is unsupported. projectUuid=${projectUuid}.`
            )
            return {
              search: (text?: string) => Promise.resolve([]),
            }
        }
      },
      [
        projectUuid,
        searchWbsItem,
        searchTicket,
        searchProjectMember,
        fetchTeams,
      ]
    )

  return { getEntitySearchReferenceEntityRepository }
}
export const useWbsItemAdditionalPropertiesBySingleType = (
  projectUuid: string,
  wbsItemType: WbsItemTypeVO
) => {
  const wbsItemTypeUuids = useMemo(() => {
    if (wbsItemType.baseTicketUuid) {
      return [wbsItemType.baseTicketUuid]
    } else {
      return [wbsItemType.uuid]
    }
  }, [wbsItemType])
  return useWbsItemAdditionalProperties(projectUuid, wbsItemTypeUuids)
}
